import type { Router } from '@remix-run/router';
import { useEffect, useState } from 'react';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { getCareflowToken } from 'src/careflow/ph-core/authentication/util/token';
import { isValidToken } from 'src/careflow/ph-core/authentication/util/jwt';

const ICOMX_BLOCKED_ROUTE_SEGMENTS = [
  'select-organization-n-role',
  'select-tenant',
  '/login',
  '/auth/',
] as const;

export interface IcomxAvailability {
  isLoggedIn: boolean;
  hasCompletedLoginSetup: boolean;
}

export function isIcomxBlockedPath(pathname: string) {
  return ICOMX_BLOCKED_ROUTE_SEGMENTS.some((segment) => pathname.includes(segment));
}

export function getIcomxAvailability(params: {
  isInitialized?: boolean;
  hasValidCareflowToken?: boolean;
  user?: unknown;
  pathname?: string;
}): IcomxAvailability {
  const isLoggedIn = !!params.isInitialized && !!params.hasValidCareflowToken && !!params.user;
  const pathname = params.pathname ?? '';
  const hasCompletedLoginSetup = isLoggedIn && !isIcomxBlockedPath(pathname);

  return {
    isLoggedIn,
    hasCompletedLoginSetup,
  };
}

export function useIcomxAvailability(router: Router) {
  const { isInitialized, getCurrentUser } = useAuth();
  const [pathname, setPathname] = useState(router.state.location.pathname);

  useEffect(() => {
    setPathname(router.state.location.pathname);
    return router.subscribe(({ location }) => setPathname(location.pathname));
  }, [router]);

  const careflowToken = getCareflowToken(true);
  let hasValidCareflowToken = false;

  try {
    hasValidCareflowToken = !!careflowToken && isValidToken(careflowToken);
  } catch (error) {
    hasValidCareflowToken = false;
  }

  return getIcomxAvailability({
    isInitialized,
    hasValidCareflowToken,
    user: getCurrentUser(),
    pathname,
  });
}
